import React, { useState, useMemo, useCallback, useEffect, useContext } from "react";
import { useDeckLink } from "react-deck-link";
import { captureException } from "@sentry/react";
import * as v0 from "@aidkitorg/types/lib/survey";
import { Button } from "@aidkitorg/component-library";
import { usePost } from "../API";
import { useModularMarkdown } from "../Hooks/ModularMarkdown";
import InterfaceContext from "../Context";
import { DistroQuestionProps } from "./Props";

export function DeckVerification({
  component,
  info,
  setInfo,
}: DistroQuestionProps<v0.DeckVerification>) {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const createLinkToken = usePost("/deck/create_link_token");
  const context = useContext(InterfaceContext);
  const currentState = info[component.targetField] || "";

  const header = useModularMarkdown({ content: component.header, info });
  const questionDescription = useModularMarkdown({ content: component.questionDescription, info });
  const connectedDescription = useModularMarkdown({ content: component.connectedDescription, info });
  const optedOutDescription = useModularMarkdown({ content: component.optedOutDescription, info });
  const verifyButtonText = useModularMarkdown({ content: component.verifyButtonText, info, noParagraphs: true });
  const reverifyButtonText = useModularMarkdown({ content: component.reverifyButtonText, info, noParagraphs: true });
  const optOutButtonText = useModularMarkdown({ content: component.optOutButtonText, info, noParagraphs: true });

  const description = useMemo(() => {
    switch (currentState) {
      case "pending":
      case "processed":
        return connectedDescription;
      case "opted_out":
        return optedOutDescription;
      default:
        return questionDescription;
    }
  }, [currentState, questionDescription, connectedDescription, optedOutDescription]);

  const primaryButtonText = ["pending", "processed"].includes(currentState)
    ? reverifyButtonText
    : verifyButtonText;

  const handleClickVerify = useCallback(async () => {
    const response = await createLinkToken({ lang: context.lang, type: component.verificationType.type });

    if (response) {
      setLinkToken(response.link_token);
    }
  }, [createLinkToken]);

  const handleClickOptOut = useCallback(() => {
    setInfo(component.targetField, "opted_out");
  }, [setInfo]);

  const handleSubmit = useCallback((connectionId: string) => {
    setLinkToken(null);
    setInfo(component.targetField, "pending");
    setInfo(component.targetField + "_connection_id", connectionId);
  }, [setInfo]);

  return (
    <fieldset>
      <legend>
        {header}
        {description}
      </legend>
      {linkToken &&
        <DeckWidget
          targetField={component.targetField}
          token={linkToken}
          onSubmit={handleSubmit}
        />
      }
      <div className="mt-2">
        <Button
          variant="primary"
          onClick={handleClickVerify}
        >
          {primaryButtonText}
        </Button>
      </div>

      {!currentState && // Applicants can opt-out if they haven't answered this question before
        <div className="mt-2">
          <Button
            variant="secondary"
            onClick={handleClickOptOut}
          >
            {optOutButtonText}
          </Button>
        </div>
      }
    </fieldset>
  );
};

interface DeckWidgetProps {
  targetField: string;
  token: string;
  onSubmit: (connectionId: string) => void;
}

function DeckWidget({
  targetField,
  token,
  onSubmit,
}: DeckWidgetProps) {
  const saveConnectionDetails = usePost("/deck/save_connection_details");

  const handleSuccess = useCallback(async ({ public_token }: { public_token?: string }) => {
    if (!public_token) {
      return;
    }

    const response = await saveConnectionDetails({ public_token, target_field: targetField });

    if (response) {
      onSubmit(response.connection_id);
    }
  }, [targetField, saveConnectionDetails, onSubmit]);

  const { isReady, isError, open } = useDeckLink({
    token,
    onSuccess: handleSuccess,
  });

  useEffect(() => {
    if (isReady) {
      open();
    }
  }, [isReady]);

  useEffect(() => {
    if (isError) {
      captureException(new Error("Deck widget encountered an error"));
    }
  }, [isError]);

  return null;
};
